// src/pages/LicensePage.jsx
import React, { useEffect, useState } from 'react';
import PageContainer from '../layouts/PageContainer';
import PageHeader from '../components/Nav/PageHeader';
import { useAuth } from '../DataStore/AuthContext';
import AlertConfirmPayment from '../components/Config/AlertConfirmPayment';
import ChangePlanModal from '../components/Config/ChangePlanModal';

const LicensePage = () => {
  const { licenseInfo, getLicenseInfo, connectedProfiles } = useAuth();
  const [selectedTopup, setSelectedTopup] = useState(1); // months
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [pendingPayment, setPendingPayment] = useState(null);
  const [changePlanModalOpen, setChangePlanModalOpen] = useState(false);

  // Fetch license info on mount
  useEffect(() => {
    getLicenseInfo(true);
  }, []);

  const handleRefresh = () => {
    getLicenseInfo(true);
  };

  const handleTopupClick = () => {
    setPendingPayment({
        months: selectedTopup,
        amount: licenseSummary.amount * selectedTopup,
        currency: licenseSummary.currency,
        planName: licenseSummary.planName,
        organisation: licenseSummary.organisation,
        organisation_id: connectedProfiles?.org_id,
    });
    setPaymentModalOpen(true);
  };

  const renderStatusBadge = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'active') {
      return <span className="badge bg-success-light text-success">Active</span>;
    }
    if (s === 'trial') {
      return <span className="badge bg-info-light text-info">Trial</span>;
    }
    if (s === 'grace') {
      return <span className="badge bg-warning-light text-warning">In Grace</span>;
    }
    return <span className="badge bg-danger-light text-danger">Expired</span>;
  };

  const renderInvoiceStatusBadge = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'paid') {
      return <span className="badge bg-success-light text-success">Paid</span>;
    }
    if (s === 'pending') {
      return <span className="badge bg-warning-light text-warning">Pending</span>;
    }
    return <span className="badge bg-secondary-light text-muted">Cancelled</span>;
  };

  const licenseSummary = licenseInfo?.licenseSummary || {
    organisation: 'Loading…',
    planName: 'OpenFi Standard',
    amount: 0,
    currency: 'UGX',
    billingCycle: 'Monthly',
    status: 'trial',
    licenseValidUntil: '—',
    graceUntil: null,
    nextBillingDate: '—',
    prepaidMonths: 0,
  };

  const invoices = licenseInfo?.invoices || [];

  const formatCurrency = (value) =>
    `${licenseSummary.currency} ${Number(value || 0).toLocaleString('en-UG')}`;

  return (
    <>
    <PageContainer>
      <PageHeader
        title="Billing & License"
        breadcrumb={
          <li className="breadcrumb-item active" aria-current="page">
            Billing & License
          </li>
        }
        primaryButtons={[
          {
            label: 'Refresh',
            icon: 'fas fa-sync',
            className: 'btn-outline-secondary',
            onClick: handleRefresh,
          },
        ]}
      />

      {/* NexumPay reminder */}
      <div className="alert alert-info d-flex align-items-center mb-3" role="alert">
        <i className="fe fe-credit-card mr-2 tx-18" />
        <div>
          <strong>NexumPay Wallet Required.</strong>{' '}
          All license payments are processed via NexumPay. Please ensure your NexumPay
          wallet has at least <b>{formatCurrency(licenseSummary.amount)}</b> before
          topping up your license.
        </div>
      </div>

      <div className="row">
        {/* Left: License summary */}
        <div className="col-xl-4 col-lg-5 mb-3">
          <div className="card custom-card shadow-sm border-0">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <h6 className="mb-1">Current License</h6>
                  <p className="mb-1 text-muted tx-12">
                    {licenseSummary.organisation}
                  </p>
                </div>
                {renderStatusBadge(licenseSummary.status)}
              </div>

              <div
                className="p-3 rounded mb-3"
                style={{ background: 'linear-gradient(135deg, #1f2430, #23293a)' }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                    <div className="text-uppercase tx-11 text-muted mb-1">
                    Plan
                    </div>
                    <h6 className="mb-0 text-white">
                    {licenseSummary.planName}
                    </h6>
                    <small className="d-block text-white-50">
                    {licenseSummary.billingCycle} · 1 account
                    </small>
                </div>
                <div className="text-right">
                    <button
                    type="button"
                    className="btn btn-xs btn-outline-light"
                    onClick={() => setChangePlanModalOpen(true)}
                    >
                    <i className="fe fe-layers mr-1" /> Change Plan
                    </button>
                    <div className="mt-2">
                    <div className="tx-11 text-muted mb-1 text-white-50">
                        Price
                    </div>
                    <h5 className="mb-0 text-white">
                        {formatCurrency(licenseSummary.amount)}
                    </h5>
                    <small className="d-block text-white-50">
                        per month
                    </small>
                    </div>
                </div>
                </div>


                <div className="row mt-2">
                  <div className="col-6">
                    <small className="d-block text-white-50 tx-11">
                      Licensed until
                    </small>
                    <span className="text-white tx-13">
                      {licenseSummary.licenseValidUntil || '—'}
                    </span>
                  </div>
                  <div className="col-6 text-right">
                    <small className="d-block text-white-50 tx-11">
                      Next billing date
                    </small>
                    <span className="text-white tx-13">
                      {licenseSummary.nextBillingDate || '—'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="tx-12 text-muted">Prepaid coverage</span>
                  <span className="tx-12">
                    {licenseSummary.prepaidMonths} month(s)
                  </span>
                </div>
                <div className="progress" style={{ height: 6 }}>
                  <div
                    className="progress-bar bg-primary"
                    role="progressbar"
                    style={{ width: '60%' }} // still a visual dummy; could be calculated later
                    aria-valuenow="60"
                    aria-valuemin="0"
                    aria-valuemax="100"
                  />
                </div>
                <small className="d-block tx-11 text-muted mt-1">
                  You are fully covered up to <b>{licenseSummary.licenseValidUntil || '—'}</b>.{' '}
                  Renew early to avoid entering grace period.
                </small>
              </div>

              <div className="border-top pt-2 mt-2">
                <small className="d-block tx-11 text-muted mb-1">
                  Grace period until
                </small>
                <span className="tx-13">
                  {licenseSummary.graceUntil || '—'}
                </span>
                <p className="mt-2 mb-0 tx-12 text-muted">
                  If your license is not renewed by this date, your routers may be
                  restricted and access to OpenFi features limited until payment
                  is received.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Top up + invoice history */}
        <div className="col-xl-8 col-lg-7 mb-3">
          <div className="card custom-card shadow-sm border-0 mb-3">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <h6 className="mb-1">Top Up License via NexumPay</h6>
                  <p className="mb-0 text-muted tx-12">
                    Choose how many months to prepay. We’ll charge your NexumPay wallet
                    and extend your license accordingly.
                  </p>
                </div>
              </div>

              <div className="d-flex flex-wrap align-items-center mb-3">
                {[1, 3, 6, 12].map((months) => {
                  const isActive = selectedTopup === months;
                  return (
                    <button
                      key={months}
                      type="button"
                      className={`btn btn-sm mr-2 mb-2 ${
                        isActive ? 'btn-primary' : 'btn-outline-primary'
                      }`}
                      onClick={() => setSelectedTopup(months)}
                    >
                      {months === 1 ? '1 Month' : `${months} Months`}
                    </button>
                  );
                })}
              </div>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <small className="d-block tx-11 text-muted">
                    Estimated amount
                  </small>
                  <strong className="tx-15">
                    {formatCurrency(licenseSummary.amount * selectedTopup)}
                  </strong>
                  <small className="d-block tx-11 text-muted">
                    Billed once via NexumPay, covers {selectedTopup}{' '}
                    {selectedTopup === 1 ? 'month' : 'months'} in advance.
                  </small>
                </div>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleTopupClick}
                >
                  <i className="fe fe-credit-card mr-1" />
                  Pay with NexumPay
                </button>
              </div>

              <div className="alert alert-warning mb-0 py-2">
                <i className="fe fe-alert-triangle mr-1" />
                <small className="tx-12">
                  Make sure your NexumPay wallet has at least{' '}
                  <b>{formatCurrency(licenseSummary.amount * selectedTopup)}</b> before
                  proceeding. Payments are instant and non-refundable.
                </small>
              </div>
            </div>
          </div>

          <div className="card custom-card shadow-sm border-0">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-1">Recent License Invoices</h6>
                <small className="text-muted tx-11">
                  Showing last {invoices.length} billing period{invoices.length === 1 ? '' : 's'}
                </small>
              </div>
              <div className="table-responsive">
                <table className="table table-sm table-hover mb-0 align-middle">
                  <thead className="thead-light">
                    <tr>
                      <th>Period</th>
                      <th>Billing Dates</th>
                      <th className="text-right">Amount</th>
                      <th>Status</th>
                      <th>Payment Ref</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr key={inv.id}>
                        <td>{inv.period}</td>
                        <td className="tx-12 text-muted">
                          {inv.start} &nbsp;–&nbsp; {inv.end}
                        </td>
                        <td className="text-right">
                          {formatCurrency(inv.amount)}
                        </td>
                        <td>
                          {renderInvoiceStatusBadge(inv.status)}
                          {inv.paidAt && (
                            <small className="d-block tx-10 text-muted">
                              Paid on {inv.paidAt}
                            </small>
                          )}
                        </td>
                        <td className="tx-12 text-muted">
                          {inv.paymentRef || <span className="text-muted">–</span>}
                        </td>
                      </tr>
                    ))}
                    {invoices.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-3">
                          No invoices yet. Your first license invoice will appear
                          here once generated.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

        </div>
      </div>
    </PageContainer>
    <AlertConfirmPayment
        show={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        payment={pendingPayment}
        onSuccess={() => {
            // maybe refresh license info
            getLicenseInfo(true);
        }}
    />
    <ChangePlanModal
        show={changePlanModalOpen}
        onClose={() => setChangePlanModalOpen(false)}
        plans={licenseInfo?.plans || []}
        currentPlanId={licenseSummary.planId || licenseSummary.plan_id || null}
        currentPlanName={licenseSummary.planName}
        organisationId={connectedProfiles?.org_id}
        onSuccess={() => {
            getLicenseInfo(true);
        }}
        />

    </>
  );
};

export default LicensePage;
